import { useLocalStorageValue } from '@react-hookz/web';
import { type ZodSchema, z } from 'zod';

/**
 * Type-safe wrapper around `useLocalStorageValue` that parses the value using a Zod schema.
 *
 * Should catch any errors and return default value if any errors occur.
 * Errors may be: Invalid JSON value in local storage, invalid value according to schema, etc.
 *
 * @see useLocalStorageEnumValue
 * @see useLocalStorageBooleanValue
 */
export function useLocalStorageParsedValue<T extends ZodSchema>(
  localStorageKey: string,
  schema: T,
  defaultValue: z.output<T>
) {
  return useLocalStorageValue(localStorageKey, {
    defaultValue,
    parse: (localStorageStringValue) => {
      let parsedValue = '';

      try {
        parsedValue = JSON.parse(localStorageStringValue ?? '""');
      } catch {
        // ignore error
      }

      return schema.catch(defaultValue).parse(parsedValue);
    },
  });
}

/**
 * Makes it easier to use an enum value from local storage.
 *
 * @see useLocalStorageParsedValue
 */
export function useLocalStorageEnumValue<const T extends Readonly<[string, ...string[]]>>(
  localStorageKey: string,
  values: T,
  defaultValue: T[number]
) {
  return useLocalStorageParsedValue(localStorageKey, z.enum(values), defaultValue);
}

/**
 * Makes it easier to use a boolean value from local storage.
 *
 * @see useLocalStorageParsedValue
 */
export function useLocalStorageBooleanValue(localStorageKey: string, defaultValue: boolean) {
  return useLocalStorageParsedValue(localStorageKey, z.boolean(), defaultValue);
}
